import { isCancel } from 'axios';
import { computed, onUnmounted, watch, ref, type Ref } from 'vue';
import { debounce } from 'lodash-es';
import { useDebounceFn, toValue, type MaybeRefOrGetter } from '@vueuse/core';
import Validator from '@/modules/common/utils/validator.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { INPUT_DEBOUNCE } from '@/modules/common/consts/ui.ts';
import { CounterpartyApi, type CounterpartyNameMatch } from '@/modules/counterparty/api/counterparty.api.ts';
import { useAbort } from '@/modules/common/composables/use-abort.ts';

export const COUNTERPARTY_NAME_DUPLICATE_ERROR = 'counterparty.master.steps.name.field.duplicate';
export const COUNTERPARTY_INN_DUPLICATE_ERROR = 'counterparty.master.steps.name.legalData.innDuplicate';

const SHOW_ERRORS_RECHECK_MS = 300;

type Options = {
  showErrors?: MaybeRefOrGetter<boolean>;
  normalize: (value: string) => string;
  canCheck?: (normalized: string) => boolean;
  exists: (normalized: string, signal: AbortSignal) => Promise<boolean>;
};

function isDuplicateCheckAborted(error: unknown, signal: AbortSignal) {
  return signal.aborted || isCancel(error);
}

function reportDuplicateCheckError(error: unknown) {
  ErrorUtil.reportError(error, { unexpectedToast: true });
}

function useDuplicateCheck(value: Ref<string>, options: Options) {
  const { signal, abort } = useAbort();
  const isDuplicate = ref(false);
  const isChecked = ref(!options.normalize(value.value));
  let checkedValue = '';

  function invalidate(normalized: string) {
    abort();
    isDuplicate.value = false;
    checkedValue = '';
    isChecked.value = !normalized;
  }

  watch(value, (next, prev) => {
    const normalized = options.normalize(next);
    if (normalized === options.normalize(prev ?? '')) {
      return;
    }

    invalidate(normalized);
  });

  async function check() {
    const normalized = options.normalize(value.value);
    if (!normalized) {
      isDuplicate.value = false;
      checkedValue = '';
      isChecked.value = true;
      return false;
    }

    if (normalized === checkedValue && isChecked.value) {
      return isDuplicate.value;
    }

    if (options.canCheck && !options.canCheck(normalized)) {
      isDuplicate.value = false;
      checkedValue = normalized;
      isChecked.value = true;
      return false;
    }

    abort();
    const requestSignal = signal.value;

    try {
      const exists = await options.exists(normalized, requestSignal);

      if (requestSignal.aborted || options.normalize(value.value) !== normalized) {
        return isDuplicate.value;
      }

      isDuplicate.value = exists;
      checkedValue = normalized;
      isChecked.value = true;
      return exists;
    } catch (error) {
      if (isDuplicateCheckAborted(error, requestSignal)) {
        return isDuplicate.value;
      }

      reportDuplicateCheckError(error);
      isDuplicate.value = false;
      checkedValue = '';
      isChecked.value = false;
      return false;
    }
  }

  function reset() {
    invalidate(options.normalize(value.value));
  }

  if (options.showErrors !== undefined) {
    const recheckIfNeeded = useDebounceFn(() => {
      if (!toValue(options.showErrors) || isChecked.value || !options.normalize(value.value)) {
        return;
      }

      void check();
    }, SHOW_ERRORS_RECHECK_MS);

    watch(
      () => toValue(options.showErrors),
      (show) => {
        if (!show || !options.normalize(value.value) || isChecked.value) {
          return;
        }

        void check();
      }
    );

    watch(isChecked, (checked) => {
      if (!checked && toValue(options.showErrors)) {
        recheckIfNeeded();
      }
    });
  }

  return { isDuplicate, isChecked, check, reset };
}

function isExactNameMatch(name: string, candidate: string) {
  return name.trim().toLocaleLowerCase() === candidate.trim().toLocaleLowerCase();
}

export function useCounterpartyNameDuplicate(name: Ref<string>, options: Pick<Options, 'showErrors'> = {}) {
  const { signal, abort } = useAbort();
  const isDuplicate = ref(false);
  const isChecked = ref(!name.value.trim());
  const isLoading = ref(false);
  const matches = ref<CounterpartyNameMatch[]>([]);
  let checkedValue = '';

  function invalidate(normalized: string) {
    abort();
    isDuplicate.value = false;
    matches.value = [];
    checkedValue = '';
    isChecked.value = !normalized;
    isLoading.value = false;
  }

  async function runCheck() {
    const normalized = name.value.trim();
    if (!normalized) {
      isDuplicate.value = false;
      matches.value = [];
      checkedValue = '';
      isChecked.value = true;
      isLoading.value = false;
      return false;
    }

    if (normalized === checkedValue && isChecked.value) {
      return isDuplicate.value;
    }

    abort();
    const requestSignal = signal.value;
    isLoading.value = true;

    try {
      const [existsResult, foundResult] = await Promise.allSettled([
        CounterpartyApi.checkNameExists(normalized, requestSignal),
        CounterpartyApi.searchByName(normalized, requestSignal),
      ]);

      if (requestSignal.aborted || name.value.trim() !== normalized) {
        return isDuplicate.value;
      }

      const rejectedReasons = [existsResult, foundResult]
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map((result) => result.reason);

      if (rejectedReasons.length) {
        if (rejectedReasons.every((reason) => isDuplicateCheckAborted(reason, requestSignal))) {
          return isDuplicate.value;
        }

        const error =
          rejectedReasons.find((reason) => !isDuplicateCheckAborted(reason, requestSignal)) ?? rejectedReasons[0];
        reportDuplicateCheckError(error);
        matches.value = [];
        isDuplicate.value = false;
        checkedValue = '';
        isChecked.value = false;
        return false;
      }

      const found = foundResult.status === 'fulfilled' ? foundResult.value : [];
      const existsByCheck = existsResult.status === 'fulfilled' ? existsResult.value : false;
      const existsByExact = found.some((item) => isExactNameMatch(normalized, item.name));

      matches.value = found;
      isDuplicate.value = existsByCheck || existsByExact;
      checkedValue = normalized;
      isChecked.value = true;
      return isDuplicate.value;
    } finally {
      if (!requestSignal.aborted) {
        isLoading.value = false;
      }
    }
  }

  const scheduleCheck = debounce(() => {
    if (!name.value.trim()) {
      return;
    }

    void runCheck();
  }, INPUT_DEBOUNCE);

  async function check() {
    scheduleCheck.cancel();
    return runCheck();
  }

  watch(name, (next, prev) => {
    const normalized = next.trim();
    if (normalized === (prev ?? '').trim()) {
      return;
    }

    invalidate(normalized);

    if (normalized) {
      scheduleCheck();
      return;
    }

    scheduleCheck.cancel();
  });

  function reset() {
    scheduleCheck.cancel();
    invalidate(name.value.trim());
  }

  onUnmounted(() => scheduleCheck.cancel());

  const similarMatches = computed(() => {
    if (isDuplicate.value) {
      return [];
    }

    return matches.value.filter((item) => !isExactNameMatch(name.value, item.name));
  });

  if (options.showErrors !== undefined) {
    const recheckIfNeeded = useDebounceFn(() => {
      if (!toValue(options.showErrors) || isChecked.value || !name.value.trim()) {
        return;
      }

      void check();
    }, SHOW_ERRORS_RECHECK_MS);

    watch(
      () => toValue(options.showErrors),
      (show) => {
        if (!show || !name.value.trim() || isChecked.value) {
          return;
        }

        void check();
      }
    );

    watch(isChecked, (checked) => {
      if (!checked && toValue(options.showErrors)) {
        recheckIfNeeded();
      }
    });
  }

  return { isDuplicate, isChecked, isLoading, matches, similarMatches, check, reset };
}

export function useCounterpartyInnDuplicate(inn: Ref<string>, options: Pick<Options, 'showErrors'> = {}) {
  return useDuplicateCheck(inn, {
    normalize: (value) => value.replace(/\D/gu, ''),
    canCheck: (safeInn) => !Validator.inn(safeInn),
    exists: (safeInn, signal) => CounterpartyApi.checkInnExists(safeInn, signal),
    showErrors: options.showErrors,
  });
}
